import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import RoomCodeEntry from './components/RoomCodeEntry';
import JoinGame from './components/JoinGame';
import GameLobby from './components/GameLobby';
import GameRoom from './components/GameRoom';
import ScrollToTop from './components/ScrollToTop';
import AgeGate from './components/AgeGate';
import ErrorBoundary from './components/common/ErrorBoundary';
import DevBuildBadge from './components/common/DevBuildBadge';
import BrowserWarning from './components/BrowserWarning';
import Footer from './components/Footer';
import Contact from './components/Contact';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import FAQ from './components/FAQ';
import About from './components/About';
import CommunityGuidelines from './components/CommunityGuidelines';
import DeckBuilderPage from './pages/DeckBuilderPage';
import OfficialCardDirectory from './components/OfficialCardDirectory';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import ChooseUsername from './components/ChooseUsername';
import Admin from './pages/Admin';
import Settings from './components/Settings';
import Steak from './components/Steak';
import Toast from './components/common/Toast';
import socketClient from './services/socketClient';
import { initializeGA, toggleDeveloperMode } from './services/analytics';
import './App.css';

// Inner component that has access to useLocation
function AppContent({ isAgeVerified }) {
  const location = useLocation();

  // Public routes that don't require age verification (legal compliance + deck builder utility + auth + verification)
  const publicRoutes = [
    '/terms',
    '/privacy',
    '/contact',
    '/about',
    '/faq',
    '/guidelines',
    '/deck-builder',
    '/official-cards',
    '/auth/callback',
    '/choose-username',
    '/admin',
    '/settings',
    '/steak',
  ];
  const isPublicRoute = publicRoutes.some((route) => location.pathname.startsWith(route));

  // Hide footer only during active gameplay to prevent mobile overlay issues
  const hideFooterRoutes = ['/game-room'];
  const shouldHideFooter = hideFooterRoutes.some((route) => location.pathname.startsWith(route));

  return (
    <div className="App">
      {/* Age verification gate (applies to all routes) */}
      <main>
        {!isAgeVerified && !isPublicRoute ? (
          <AgeGate />
        ) : (
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/play" element={<LandingPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/choose-username" element={<ChooseUsername />} />
            <Route path="/create-room" element={<RoomCodeEntry />} />
            <Route path="/join-game" element={<JoinGame />} />
            <Route path="/game-lobby" element={<GameLobby />} />
            <Route path="/game-room" element={<GameRoom />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/about" element={<About />} />
            <Route path="/guidelines" element={<CommunityGuidelines />} />
            <Route path="/deck-builder" element={<DeckBuilderPage />} />
            <Route path="/official-cards" element={<OfficialCardDirectory />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/steak" element={<Steak />} />
          </Routes>
        )}
      </main>
      {/* Hide footer only during active gameplay to prevent mobile overlay */}
      {!shouldHideFooter && <Footer />}
    </div>
  );
}

function App() {
  const [isAgeVerified, setIsAgeVerified] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  // COLD START FIX: Toast notification state
  const [toast, setToast] = useState(null);

  // COLD START FIX: Monitor connection status and show notifications
  // Use useLayoutEffect to register callback BEFORE child components mount
  React.useLayoutEffect(() => {
    const handleConnectionStatus = (status, data) => {
      console.log('🔔 Connection status:', status, data);

      switch (status) {
        case 'connecting':
          // Don't show toast for initial connection (too noisy)
          break;

        case 'cold-start':
          setToast({
            message: data.message || 'Waking up server... This may take up to 30 seconds',
            type: 'warning',
            duration: 8000, // Show longer for cold starts
          });
          break;

        case 'connected':
          if (data.wasColdStart) {
            setToast({
              message: data.message || 'Server ready!',
              type: 'success',
              duration: 3000,
            });
          } else {
            // TEMP: Show confirmation for warm starts too (for testing)
            setToast({
              message: 'Server already running ✓',
              type: 'info',
              duration: 2000, // Shorter duration
            });
          }
          break;

        case 'error':
          // Only show error toast in production (might be server sleeping)
          if (process.env.NODE_ENV === 'production') {
            setToast({
              message: 'Connection issue - trying to reconnect...',
              type: 'error',
              duration: 5000,
            });
          }
          break;

        default:
          break;
      }
    };

    // Subscribe to connection status updates EARLY (before children mount)
    const unsubscribe = socketClient.onConnectionStatus(handleConnectionStatus);
    console.log('🔔 Registered connection status callback in App.js');

    return () => {
      unsubscribe();
    };
  }, []);

  // Check for age verification
  useEffect(() => {
    const checkAccess = () => {
      // Check age verification
      const ageVerified = localStorage.getItem('ageVerified');
      const ageVerifiedTime = localStorage.getItem('ageVerifiedTime');

      if (ageVerified === 'true' && ageVerifiedTime) {
        const timeDiff = Date.now() - parseInt(ageVerifiedTime);
        const dayInMs = 24 * 60 * 60 * 1000;

        // Age verification expires after 30 days
        if (timeDiff > dayInMs * 30) {
          localStorage.removeItem('ageVerified');
          localStorage.removeItem('ageVerifiedTime');
          setIsAgeVerified(false);
        } else {
          setIsAgeVerified(true);
        }
      } else {
        setIsAgeVerified(false);
      }
      setIsCheckingAccess(false);
    };

    checkAccess();

    const interval = setInterval(checkAccess, 60000);
    return () => clearInterval(interval);
  }, []);

  // Initialize Google Analytics
  useEffect(() => {
    if (isAgeVerified) {
      initializeGA();
      console.log('📊 Google Analytics initialized');
    }
  }, [isAgeVerified]);

  // Optional: Add keyboard shortcut for developer mode toggle (Cmd+Shift+D)
  useEffect(() => {
    if (isAgeVerified) {
      const handleKeyPress = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'D') {
          const newMode = toggleDeveloperMode();
          console.log(`🔧 Developer mode: ${newMode ? 'ENABLED' : 'DISABLED'}`);
          // Refresh to apply new tracking mode
          setTimeout(() => window.location.reload(), 100);
        }
      };

      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [isAgeVerified]);

  if (isCheckingAccess) {
    return (
      <div className="page-container">
        <div style={{ color: 'white', fontSize: '1.2rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <AppContent isAgeVerified={isAgeVerified} />
      </Router>
      <DevBuildBadge />
      {/* #237: warns users on DuckDuckGo/Opera Mini/IE that WebSocket connections
          may be blocked - route-independent, so it lives alongside the other
          global overlays here rather than inside AppContent. */}
      <BrowserWarning />
      {/* COLD START FIX: Show connection status toasts */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => setToast(null)}
        />
      )}
    </ErrorBoundary>
  );
}

export default App;
